/**
 * Dates and times for the bookings pages (7 October 2026).
 *
 * Every time on these pages is read in the organisation's time zone, not the
 * browser's: a receptionist in Durban and an owner travelling in London must
 * see the same 09:00 for the same booking. A local date is `yyyy-MM-dd` and a
 * local time `HH:mm`; an instant is an ISO string with its offset, as the
 * service sends and expects them.
 *
 * Pure functions only, so the grid layout and the zone arithmetic can be
 * tested without a browser.
 */

export const DEFAULT_TZ = 'Africa/Johannesburg';

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    partsFormatters.set(tz, f);
  }
  return f;
}

/** The wall-clock reading of an instant in a time zone. */
export function zonedParts(input: Date | string, tz: string): ZonedParts {
  const d = typeof input === 'string' ? new Date(input) : input;
  const out: Record<string, number> = {};
  for (const p of partsFormatter(tz).formatToParts(d)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year ?? 1970,
    month: out.month ?? 1,
    day: out.day ?? 1,
    hour: (out.hour ?? 0) % 24,
    minute: out.minute ?? 0
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `yyyy-MM-dd` of an instant in the zone. */
export function localDateOf(input: Date | string, tz: string): string {
  const p = zonedParts(input, tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Minutes since local midnight of an instant in the zone. */
export function minutesOfDay(input: Date | string, tz: string): number {
  const p = zonedParts(input, tz);
  return p.hour * 60 + p.minute;
}

/** `HH:mm` of an instant in the zone. */
export function localTimeOf(input: Date | string, tz: string): string {
  return hhmm(minutesOfDay(input, tz));
}

/** How far the zone is ahead of UTC at an instant, in minutes. */
export function offsetMinutes(at: Date, tz: string): number {
  const p = zonedParts(at, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const actual = Math.floor(at.getTime() / 60_000) * 60_000;
  return Math.round((asUtc - actual) / 60_000);
}

/** The instant a local date and time stand for in the zone. */
export function zonedToDate(date: string, time: string, tz: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const mins = parseHhmm(time) ?? 0;
  const wall = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, Math.floor(mins / 60), mins % 60);
  // Two passes settle the offset, also across a daylight-saving change.
  let guess = new Date(wall - offsetMinutes(new Date(wall), tz) * 60_000);
  guess = new Date(wall - offsetMinutes(guess, tz) * 60_000);
  return guess;
}

/** ISO with the zone's offset, such as `2026-10-12T09:00:00+02:00`. */
export function zonedIso(date: string, time: string, tz: string): string {
  const at = zonedToDate(date, time, tz);
  const off = offsetMinutes(at, tz);
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  const mins = parseHhmm(time) ?? 0;
  return `${date}T${hhmm(mins)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** A local date `n` days on (or back). */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** 1 for Monday to 7 for Sunday. */
export function isoWeekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  const w = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  return w === 0 ? 7 : w;
}

/** The Monday on or before a local date. */
export function startOfWeek(date: string): string {
  return addDays(date, 1 - isoWeekday(date));
}

/** Today's local date in the zone. */
export function todayIn(tz: string, now: Date = new Date()): string {
  return localDateOf(now, tz);
}

/** The local dates from `from`, `count` of them. */
export function daysFrom(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addDays(from, i));
}

export function hhmm(minutes: number): string {
  const m = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** Minutes of `HH:mm`, or null when it is not a time. */
export function parseHhmm(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
}

/** The ISO instants that bound local days `from` (inclusive) to `toExclusive`. */
export function rangeIso(from: string, toExclusive: string, tz: string): { from: string; to: string } {
  return { from: zonedIso(from, '00:00', tz), to: zonedIso(toExclusive, '00:00', tz) };
}

/** A local date in words, read as that calendar day wherever the browser is. */
export function formatLocalDate(date: string, locale: string, opts: Intl.DateTimeFormatOptions): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12));
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' }).format(t);
}

/** An instant in words, in the zone. */
export function formatInZone(input: Date | string, tz: string, locale: string, opts: Intl.DateTimeFormatOptions): string {
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: tz }).format(d);
}

// ── Calendar grid ─────────────────────────────────────────────────────────

export interface GridItem {
  id: string;
  /** Minutes since midnight. */
  start: number;
  end: number;
}

export type Placed<T extends GridItem> = T & {
  /** Which column the item sits in among those it overlaps. */
  column: number;
  /** How many columns its overlapping group needs. */
  columns: number;
};

/**
 * Side-by-side placement for one day's blocks: items that overlap share the
 * width, each in the first free column, and every item in an overlapping group
 * is told the group's column count so their widths agree.
 */
export function layoutDay<T extends GridItem>(items: readonly T[]): Placed<T>[] {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end || a.id.localeCompare(b.id));
  const out: Placed<T>[] = [];
  let group: Placed<T>[] = [];
  let groupEnd = -Infinity;
  const columnEnds: number[] = [];

  const flush = () => {
    const n = Math.max(1, ...group.map((g) => g.column + 1));
    for (const g of group) g.columns = n;
    out.push(...group);
    group = [];
    columnEnds.length = 0;
  };

  for (const item of sorted) {
    const end = Math.max(item.end, item.start + 1);
    if (group.length > 0 && item.start >= groupEnd) {
      flush();
      groupEnd = -Infinity;
    }
    let col = columnEnds.findIndex((e) => e <= item.start);
    if (col === -1) {
      col = columnEnds.length;
      columnEnds.push(end);
    } else {
      columnEnds[col] = end;
    }
    group.push({ ...item, column: col, columns: 1 });
    groupEnd = Math.max(groupEnd, end);
  }
  if (group.length > 0) flush();
  return out;
}

/** The hours a day grid should show: the working day, stretched to fit every item, on whole hours. */
export function visibleHours(items: readonly GridItem[], fallback: { from: number; to: number } = { from: 7 * 60, to: 18 * 60 }): { from: number; to: number } {
  let from = fallback.from;
  let to = fallback.to;
  for (const i of items) {
    from = Math.min(from, i.start);
    to = Math.max(to, i.end);
  }
  return { from: Math.max(0, Math.floor(from / 60) * 60), to: Math.min(24 * 60, Math.ceil(to / 60) * 60) };
}

/** A time on the grid from a pointer position, snapped down to `step` minutes. */
export function snapMinutes(offsetPx: number, pxPerMinute: number, fromMinutes: number, step: number): number {
  const raw = fromMinutes + Math.max(0, offsetPx) / pxPerMinute;
  return Math.floor(raw / step) * step;
}

// ── Questions ─────────────────────────────────────────────────────────────

/** A question's key from its label: lower case, words joined by `_`, unique among `taken`. */
export function questionKey(label: string, taken: readonly string[] = []): string {
  const base = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'question';
  if (!taken.includes(base)) return base;
  for (let i = 2; ; i++) {
    const k = `${base}_${i}`;
    if (!taken.includes(k)) return k;
  }
}
